import { useEffect, useRef } from 'react';

/**
 * Run a callback on an interval only while the document is visible.
 * Clears the timer when the tab is hidden; on return to visible, ticks once then resumes.
 *
 * @param {() => void} callback
 * @param {number} delayMs
 * @param {boolean} [enabled=true]
 * @param {{ immediate?: boolean }} [options] - if immediate (default true), fire once when the interval starts
 */
export function useVisibleInterval(callback, delayMs, enabled = true, options = {}) {
    const { immediate = true } = options;
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
    const immediateRef = useRef(immediate);
    immediateRef.current = immediate;

    useEffect(() => {
        if (!enabled || !delayMs || delayMs < 0) return undefined;

        let intervalId = null;

        const tick = () => {
            if (document.visibilityState === 'visible') {
                try {
                    callbackRef.current();
                } catch {
                    /* ignore tick errors */
                }
            }
        };

        const start = ({ fireNow } = {}) => {
            if (intervalId != null) return;
            if (fireNow) tick();
            intervalId = setInterval(tick, delayMs);
        };

        const stop = () => {
            if (intervalId != null) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                // Always refresh when returning to the tab
                start({ fireNow: true });
            } else {
                stop();
            }
        };

        if (document.visibilityState === 'visible') {
            start({ fireNow: immediateRef.current });
        }

        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [delayMs, enabled]);
}
